// * Importing the necessary libraries and components
import { useState, useCallback, useEffect } from "react";

// * Importing the useNavigate hook
import { useNavigate } from "react-router-dom";

// * Importing the moment library
import moment from "moment";

// * Importing the List component
import { List } from "../../../component/list/list";
import { Loading } from "../../../component/common/Loading";

// * Importing the useApplications hook
import { useApplications } from "../../../hooks/applications/useApplications";

// ? Importing the AuthAPI service
import { AuthAPI } from "../../../services/apis/AuthAPI";


//* lodash debounce
const debounce = require("lodash.debounce");

// ? Application type
type Application = {
  id: string;
  name: string;
  description?: string;
  isOnline: boolean;
  status: string;
  startDate: string;
  endDate: string;
  deadlineDate: string;
};

export function ApplicationView() {
  const token = localStorage.getItem("token") || "";
  const navigate = useNavigate();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [me, setMe] = useState<any | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [applicationToDelete, setApplicationToDelete] = useState<string | null>(
    null
  );
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const {
    applications,
    loading,
    error,
    setSearch,
    pagination,
    setPagination,
    deleteApplication,
    refetch,
    changePage,
    isDeleting,
  } = useApplications(token);

  useEffect(() => {
    async function fetchMe() {
      try {
        const response = await AuthAPI.me(token);
        setMe(response.data);
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    }
    fetchMe();
  }, [token]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value.trim().length >= 3) {
        setSearch(value);
      } else {
        setSearch("");
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      }
      setIsSearching(false);
    }, 500),
    [setSearch, setPagination]
  );

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      setIsSearching(true);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (applicationToDelete) {
      try {
        const currentPageItems = applications.length;
        const currentPage = pagination.currentPage;

        await deleteApplication(applicationToDelete);

        if (currentPageItems === 1 && currentPage > 1) {
          changePage(currentPage - 1);
        }

        setShowWarning(false);
        setApplicationToDelete(null);
      } catch (error) {
        console.error("Error deleting application:", error);
      }
    }
  }, [
    applicationToDelete,
    deleteApplication,
    applications.length,
    pagination.currentPage,
    changePage,
  ]);

  const handleDeleteActionClick = useCallback((id: string) => {
    setApplicationToDelete(id);
    setShowWarning(true);
  }, []);

  const handleChangePagination = useCallback(
    (value: number) => {
      changePage(value);
    },
    [changePage]
  );

  const totalApplications = pagination.totalItems || 0;

  if (isDeleting) {
    return <Loading data-testid="loading-deleting" />;
  }

  if (error) {
    return <div data-testid="error-message">Error: {error}</div>;
  }

  return (
    <div className="row" data-testid="application-view">
      <List
        search_input_value={searchInputValue}
        search_text="Pesquise pelo nome da aplicação"
        onChangeSearchInput={handleSearchInputChange}
        onSearchButtonClick={refetch}
        title={`Aplicações (Total: ${totalApplications})`}
        not_found_message="Nenhuma aplicação encontrada"
        button_link="/tests/create"
        button_text="Adicionar aplicação"
        deleting_message="Ao deletar esta aplicação, ela não estará mais disponível no sistema"
        onDeleteCancel={() => setShowWarning(false)}
        onDeleteConfirm={handleDeleteConfirm}
        onDeleteActionClick={handleDeleteActionClick}
        onEditActionClick={(id) => navigate(`/tests/edit/${id}`)}
        onChangePagination={handleChangePagination}
        onManagerActionClick={(value) => {
          navigate(`/tests/view/${value}`, {
            state: { filter: value, key: "application_id" },
          });
        }}
        show_warning={showWarning}
        loading_screen={loading || isSearching || isDeleting}
        items={applications.map((application: Application) => ({
          ...application,
          key: application.id,
        }))}
        pagination={{
          last_page: pagination.totalPages,
          current_page: pagination.currentPage,
        }}
        fields={[
          { name: "name", type: "text", header: "Nome" },
          { name: "description", type: "text", header: "Descrição" },
          {
            name: "type",
            type: "custom",
            header: "Tipo",
            render: (item: Application) =>
              item.isOnline ? "Online" : "Presencial",
          },
          {
            name: "startDateTime",
            type: "custom",
            header: "Data e Hora de Início",
            render: (item: Application) =>
              moment(item.startDate).format("YYYY-MM-DD HH:mm"),
          },
          {
            name: "endDateTime",
            type: "custom",
            header: "Data e Hora de Término",
            render: (item: Application) =>
              moment(item.endDate).format("YYYY-MM-DD HH:mm"),
          },
          {
            name: "status",
            type: "custom",
            header: "Status",
            render: (item: Application) => {
              switch (item.status) {
                case "pending":
                  return "Criada";
                case "in_process":
                  return "Iniciada";
                case "finished":
                  return "Finalizada";
                case "cancelled":
                  return "Cancelada";
                default:
                  return "Desconhecido";
              }
            },
          },
        ]}
        loading_search={isSearching}
        data-testid="applications-list"
      />
    </div>
  );
}
